/**
 * Utility for handling lazy imports that may be blocked by ad blockers
 *
 * Wraps React.lazy() with error handling to gracefully handle cases where
 * dynamic imports are blocked by browser extensions or ad blockers.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import BlockedContent from "@/components/error/BlockedContent";

/**
 * Creates a lazy-loaded component with error handling for blocked imports
 *
 * @param importFn - Function that returns a dynamic import promise
 * @param fallbackComponent - Optional fallback component to render if import fails
 * @param componentName - Optional name of the component for error messages
 * @returns Lazy component that handles blocked imports gracefully
 *
 * @example
 * const HomePage = createLazyComponent(
 *   () => import("./pages/HomePage"),
 *   undefined,
 *   "Home Page"
 * );
 */
export function createLazyComponent<P = Record<string, never>>(
  importFn: () => Promise<{ default: ComponentType<P> }>,
  fallbackComponent?: () => ComponentType<P> | null,
  componentName?: string
): LazyExoticComponent<ComponentType<P>> {
  return lazy(() =>
    importFn()
      .then((module) => module)
      .catch((error) => {
        // Log error to console with full details
        console.error("Failed to load module:", {
          componentName,
          error,
          message: error.message,
          stack: error.stack,
        });

        // Return fallback component if provided
        if (fallbackComponent) {
          const Fallback = fallbackComponent();
          return Fallback
            ? { default: Fallback }
            : {
                default: () => (
                  <BlockedContent componentName={componentName} error={error} />
                ),
              };
        }

        // Default: return BlockedContent component with helpful message
        return {
          default: () => <BlockedContent componentName={componentName} error={error} />,
        };
      })
  );
}
